/**
 * NumberTicker — animated counter that tweens between successive
 * numeric values.
 *
 * KPI strips, security metric callouts, and "demo result" surfaces
 * regularly need to communicate a numeric change without forcing the
 * user to read both the old and new value. The ticker eases between
 * values over a short duration so the eye picks up the trend without
 * losing the final reading.
 *
 * The component is presentation-only. It does not own the value —
 * the consumer passes a target, and the ticker animates from the
 * previous render's value to the new target whenever it changes.
 * Honors `prefers-reduced-motion`: the value snaps instantly when
 * the user opted out.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useEffect, useRef, useState } from "react";

export interface NumberTickerProps {
  /** Target value. The ticker animates from the previous value to this one. */
  readonly value: number;
  /** Animation duration in ms. Defaults to 600. */
  readonly durationMs?: number;
  /** Decimal places kept on the rendered string. Defaults to 0. */
  readonly precision?: number;
  /** Optional prefix (e.g. `$`). */
  readonly prefix?: string;
  /** Optional suffix (e.g. `%`). */
  readonly suffix?: string;
  /**
   * Optional formatter that overrides `precision` / locale formatting.
   * Receives the interpolated numeric value and returns the string
   * actually rendered.
   */
  readonly format?: (value: number) => string;
  /** Optional className passed to the rendered span. */
  readonly className?: string;
  /** Accessible label override. Defaults to the formatted final value. */
  readonly ariaLabel?: string;
}

function easeOutCubic(progress: number): number {
  const clamped = Math.min(1, Math.max(0, progress));
  return 1 - (1 - clamped) ** 3;
}

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function formatValue(
  value: number,
  precision: number,
  prefix: string | undefined,
  suffix: string | undefined,
  format: ((value: number) => string) | undefined,
): string {
  const body = format !== undefined ? format(value) : value.toFixed(precision);
  return `${prefix ?? ""}${body}${suffix ?? ""}`;
}

/** Eases the rendered number between value changes. */
export const NumberTicker = React.memo(function NumberTicker({
  value,
  durationMs = 600,
  precision = 0,
  prefix,
  suffix,
  format,
  className,
  ariaLabel,
}: NumberTickerProps): React.JSX.Element {
  const [displayValue, setDisplayValue] = useState<number>(value);
  const frameRef = useRef<number | null>(null);
  const previousRef = useRef<number>(value);

  useEffect(() => {
    if (value === previousRef.current) return;
    if (prefersReducedMotion()) {
      previousRef.current = value;
      setDisplayValue(value);
      return;
    }

    const startValue = previousRef.current;
    const delta = value - startValue;
    const start = performance.now();

    const tick = (now: number): void => {
      const elapsed = now - start;
      const progress = elapsed / durationMs;
      if (progress >= 1) {
        previousRef.current = value;
        setDisplayValue(value);
        frameRef.current = null;
        return;
      }
      const eased = easeOutCubic(progress);
      setDisplayValue(startValue + delta * eased);
      frameRef.current = window.requestAnimationFrame(tick);
    };

    if (frameRef.current !== null) {
      window.cancelAnimationFrame(frameRef.current);
    }
    frameRef.current = window.requestAnimationFrame(tick);

    return (): void => {
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [durationMs, value]);

  const rendered = formatValue(displayValue, precision, prefix, suffix, format);
  const targetRendered = formatValue(value, precision, prefix, suffix, format);

  return (
    <span
      className={className}
      data-csk-number-ticker
      style={{ fontVariantNumeric: "tabular-nums" }}
      aria-label={ariaLabel ?? targetRendered}
    >
      {rendered}
    </span>
  );
});
